import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Sparkles, Ticket, Calendar, Clock, Users, ArrowRight, AlertCircle, Loader2, Download, MapPin, Crown } from "lucide-react";
import SEOHead from "../components/SEOHead";
import Starfield from "../components/Starfield";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 6;
const POLL_INTERVAL = 2500;
const GOLD = "#D4AF37";
const CHAMPAGNE = "#F5E6C8";

export default function NYEBookingSuccess() {
 const [params] = useSearchParams();
 const sessionId = params.get("session_id");
 const [state, setState] = useState({ status: "checking", data: null, error: "" });
 const polledRef = useRef(0);

 useEffect(() => { document.title = "NYE 2026 Booking Confirmed | Magic Cruises"; }, []);

 useEffect(() => {
  if (!sessionId) { setState({ status: "error", data: null, error: "No payment session in URL" }); return; }
  let cancelled = false;
  const poll = async () => {
   try {
    try { await axios.post(`${API}/checkout/finalize/${sessionId}`); } catch (_) { /* already finalized */ }
    const r = await axios.get(`${API}/checkout/status/${sessionId}`);
    if (cancelled) return;
    const ps = r.data.payment_status;
    if (ps === "paid") { setState({ status: "paid", data: r.data, error: "" }); return; }
    if (r.data.status === "expired") { setState({ status: "expired", data: r.data, error: "Session expired" }); return; }
    polledRef.current += 1;
    if (polledRef.current >= MAX_POLLS) { setState({ status: "timeout", data: r.data, error: "Still processing, check your email" }); return; }
    setTimeout(poll, POLL_INTERVAL);
   } catch (e) {
    if (cancelled) return;
    setState({ status: "error", data: null, error: e?.response?.data?.detail || "Unable to verify payment" });
   }
  };
  poll();
  return () => { cancelled = true; };
 }, [sessionId]);

 const meta = state.data?.metadata || {};
 const order = state.data?.order || {};
 const amount = state.data?.amount_total ? (state.data.amount_total / 100) : (order.amount || 0);

 return (
  <main className="bg-[#06060A] text-white min-h-screen relative overflow-hidden" data-testid="nye-booking-success">
   <SEOHead title="NYE Booking Confirmation | Magic Cruises" description="Your Sydney Harbour NYE 2026 cruise booking is confirmed." />
   <div className="absolute inset-0 pointer-events-none">
    <div className="absolute top-0 left-1/4 w-[40rem] h-[40rem] rounded-full bg-amber-400/20 blur-3xl animate-pulse" />
    <div className="absolute bottom-0 right-1/4 w-[40rem] h-[40rem] rounded-full bg-fuchsia-500/15 blur-3xl animate-pulse" style={{ animationDelay: "1.5s" }} />
   </div>
   <Starfield density={70} color="#FFE9A8" glow="rgba(255,210,120,0.6)" fixed={true} intensity={0.6} />

   <div className="relative pt-32 pb-32 px-5 lg:px-10 mx-auto max-w-3xl">
    {state.status === "checking" && (
     <div className="text-center" data-testid="nye-booking-checking">
      <Loader2 className="w-12 h-12 text-amber-300 mx-auto mb-6 animate-spin" />
      <h1 className="text-3xl md:text-4xl font-black mb-3">Confirming your NYE payment…</h1>
      <p className="text-white/65 text-lg">Hold tight, this usually takes a couple of seconds.</p>
     </div>
    )}

    {state.status === "paid" && (
     <div data-testid="nye-booking-paid">
      <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center shadow-2xl mb-7" style={{ background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)`, boxShadow: "0 20px 50px -10px rgba(212,175,55,0.6)" }}>
       <Check className="w-10 h-10 text-[#0A0A0A]" strokeWidth={3} />
      </div>
      <div className="text-center mb-12">
       <div className="text-amber-300 text-[11px] uppercase tracking-[0.32em] font-bold mb-3">Payment Confirmed · NYE 2026</div>
       <h1 className="font-black tracking-tight leading-[0.98] mb-5" style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)", fontFamily: "'Cinzel Decorative', serif" }}>
        See you on <em className="italic font-light bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }}>the harbour.</em>
       </h1>
       <p className="text-white/82 text-lg leading-[1.7] max-w-xl mx-auto">
        Your NYE 2026 cruise tickets are confirmed. We've sent your boarding pass to <strong className="text-white">{meta.customer_email}</strong>.
       </p>
      </div>

      <div className="rounded-3xl ring-1 ring-amber-300/30 bg-gradient-to-br from-amber-400/[0.10] via-[#1A1108]/80 to-[#0A0618]/80 backdrop-blur-md p-7 lg:p-8 mb-8 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.35)]">
       <div className="text-amber-300 text-[11px] uppercase tracking-[0.32em] font-bold mb-5 flex items-center gap-2"><Ticket className="w-3.5 h-3.5" /> Your NYE Ticket</div>
       <dl className="space-y-4">
        <SummaryRow Icon={Calendar} label="Date" value="Thursday 31 December 2026" />
        <SummaryRow Icon={Clock} label="Boarding" value="7:30pm sharp" />
        <SummaryRow Icon={Clock} label="Returns" value="~1:30am New Year's Day" />
        <SummaryRow Icon={MapPin} label="Wharf" value="King St Wharf 3, Darling Harbour" />
        <SummaryRow Icon={Users} label="Tickets" value={`${meta.adult_qty || 0} adult · ${meta.child_qty || 0} child`} />
        <SummaryRow Icon={Crown} label="Includes" value="Front-row 9pm + midnight fireworks, premium open bar, canapés, buffet, DJ all night" />
       </dl>
       <div className="border-t border-white/15 mt-6 pt-5 flex items-baseline justify-between">
        <span className="text-white/65 uppercase tracking-widest text-[10px] font-bold">Total paid</span>
        <span className="text-3xl font-black bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }}>${amount.toLocaleString()} AUD</span>
       </div>
       <div className="text-white/45 text-[10px] uppercase tracking-widest font-bold text-right">Reference · {state.data?.session_id?.slice(-8) || ""}</div>
      </div>

      <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 mb-8 flex items-start gap-4">
       <Download className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
       <div className="flex-1">
        <div className="text-white font-bold mb-1">Your NYE boarding pass is ready</div>
        <div className="text-white/82 text-sm leading-[1.7]">Premium boarding pass with QR code. Show it on your phone at the gangway or print it.</div>
       </div>
      </div>

      <div className="flex flex-wrap gap-3 justify-center">
       <a href={`${API}/tickets/${state.data?.session_id}/pdf`} target="_blank" rel="noopener noreferrer" className="px-7 py-4 rounded-full text-[#0A0A0A] text-xs font-black uppercase tracking-[0.22em] shadow-[0_15px_40px_-10px_rgba(212,175,55,0.55)] transition-all hover:scale-[1.02] inline-flex items-center gap-2" style={{ background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }} data-testid="nye-success-download-pdf">
        <Download className="w-4 h-4" /> Download Boarding Pass
       </a>
       <Link to="/cruises/nye-cruises-sydney-harbour" className="px-7 py-4 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-[0.22em] border border-white/20 transition-colors" data-testid="nye-success-back">
        Back to NYE Cruise
       </Link>
      </div>
     </div>
    )}

    {state.status === "expired" && (
     <div className="text-center" data-testid="nye-booking-expired">
      <AlertCircle className="w-14 h-14 text-amber-300 mx-auto mb-5" />
      <h1 className="text-3xl md:text-4xl font-black mb-3">Session expired</h1>
      <p className="text-white/65 text-base mb-7">No charge was made. Please try booking again.</p>
      <Link to="/cruises/nye-cruises-sydney-harbour/book" className="inline-flex px-7 py-4 rounded-full text-[#0A0A0A] text-xs font-black uppercase tracking-[0.22em] shadow-[0_15px_40px_-10px_rgba(212,175,55,0.55)]" style={{ background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }}>Try again</Link>
     </div>
    )}

    {state.status === "timeout" && (
     <div className="text-center" data-testid="nye-booking-timeout">
      <Loader2 className="w-12 h-12 text-amber-300 mx-auto mb-5" />
      <h1 className="text-3xl md:text-4xl font-black mb-3">Still processing</h1>
      <p className="text-white/65 text-base mb-2 max-w-xl mx-auto leading-[1.7]">Your bank is taking longer than usual to confirm. Your email at <strong className="text-white">{meta.customer_email || "the address you provided"}</strong> will receive confirmation as soon as it clears. You can safely close this page.</p>
     </div>
    )}

    {state.status === "error" && (
     <div className="text-center" data-testid="nye-booking-error">
      <AlertCircle className="w-14 h-14 text-red-300 mx-auto mb-5" />
      <h1 className="text-3xl md:text-4xl font-black mb-3">Something went wrong</h1>
      <p className="text-white/65 text-base mb-7">{state.error}</p>
      <Link to="/cruises/nye-cruises-sydney-harbour/book" className="inline-flex px-7 py-4 rounded-full text-[#0A0A0A] text-xs font-black uppercase tracking-[0.22em] shadow-[0_15px_40px_-10px_rgba(212,175,55,0.55)]" style={{ background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${GOLD} 100%)` }}>Try again</Link>
     </div>
    )}
   </div>
  </main>
 );
}

function SummaryRow({ Icon, label, value }) {
 return (
  <div className="flex items-start gap-3">
   <Icon className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
   <div className="flex-1 flex justify-between gap-3 flex-wrap">
    <span className="text-white/55 uppercase tracking-widest text-[10px] font-bold">{label}</span>
    <span className="text-white font-semibold text-right max-w-md">{value}</span>
   </div>
  </div>
 );
}
